import { useTranslation } from 'react-i18next';
import { DrawingLayer } from './DrawingLayer/DrawingLayer';
import { Toolbar } from './Toolbar/Toolbar';
import { useDrawingShortcuts } from './hooks/useDrawingShortcuts';
import type { AnnotationDraft as Draft } from '../../hooks/useAnnotationDraft';
import type { MarkupItem } from '../../utils/markupTypes';
import type { ReactNode } from 'react';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './AnnotationDraft.module.css';

/**
 * children: the rendered document — the same element the viewer uses
 * backdropItems: saved marks, painted underneath and untouchable
 * onZoomDelta: zoom lives on the page, so the shortcuts reach it from here
 */
interface IAnnotationDraftProps {
  draft: Draft;
  children: ReactNode;
  contentKey: string | number;
  backdropItems: readonly MarkupItem[];
  contentReady: boolean;
  onZoomDelta: (direction: 1 | -1) => void;
}

/**
 * The drawing surface and the tools that act on it — the note and the commit
 * live in the sidebar's draft card. The saved annotations underneath are a
 * backdrop no tool can reach: removing someone else's mark means unchecking
 * their annotation, never editing it (ADR-0013).
 */
export function AnnotationDraft({
  draft, children, contentKey, backdropItems, contentReady, onZoomDelta,
}: IAnnotationDraftProps) {
  const { t } = useTranslation();
  const { markup } = draft;
  const disabled = draft.saving || !contentReady;
  useDrawingShortcuts({
    disabled: draft.saving,
    undo: markup.undo,
    redo: markup.redo,
    zoom: onZoomDelta,
    clearPage: markup.clearPage,
    hasSelection: markup.selectedIndices.length > 0,
    eraseSelected: markup.eraseSelected,
    selectTool: () => markup.setTool('select'),
  });

  return (
    <>
      <DrawingLayer
        contentKey={contentKey}
        items={markup.pageItems}
        backdropItems={backdropItems}
        tool={markup.tool}
        color={markup.activeColor}
        strokeWidth={markup.strokeWidth}
        textStyle={markup.activeTextStyle}
        selectedIndices={markup.selectedIndices}
        disabled={disabled}
        onCommit={markup.commit}
        onErase={markup.eraseAt}
        onSelect={markup.setSelectedIndices}
        onMoveItem={markup.moveItem}
        onMoveItems={markup.moveItems}
        onResizeItem={markup.resizeItem}
        textBox={markup.textBox}
        onSettleText={markup.settleText}
        textInputLabel={t('documents.markup.textInputLabel')}
      >
        {children}
      </DrawingLayer>

      <div className={styles.bar}>
        <ScrollArea orientation="horizontal" viewportClassName={styles.barBody}>
          <Toolbar
            tool={markup.tool}
            color={markup.activeColor}
            canUndo={markup.canUndo}
            canRedo={markup.canRedo}
            canClearPage={markup.pageItems.length > 0}
            disabled={disabled}
            textStyle={markup.activeTextStyle}
            strokeWidthSteps={markup.strokeWidthSteps}
            textSelected={markup.textSelected}
            onToolChange={markup.setTool}
            onColorChange={markup.changeColor}
            onStrokeWidthChange={markup.changeStrokeWidth}
            onTextStyleChange={markup.changeTextStyle}
            onUndo={markup.undo}
            onRedo={markup.redo}
            onClearPage={markup.clearPage}
          />
        </ScrollArea>
      </div>
    </>
  );
}
